// ClientFinancePage's reads and its rows' status writes (finance consolidation S7, the user 2026-10-22): the client's
// jobs and quotes, with its name, own flag and today (/clients/:id/line-jobs), its invoices (/clients/:id/job-invoices),
// and the claims and people its rows and drawers name. Main's page read (/clients/:id/page) and its contracts are no longer
// read: the month drawers, Split and Who does what are retired (2026-10-23), and the own client is set in Setup.
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useClientClaims } from "../../hooks/useJobClaims";
import { usePeople } from "../../hooks/useLedger";
import { useClientJobs, useJobInvoices, useSetInvoiceStatus, useSetJobStatus, useSetQuoteStatus, useUpdateQuote } from "../../hooks/useLineJobs";
import type {
  ClientJobInvoicesResponse, ClientJobsResponse, JobClaim, JobInvoice, JobInvoiceStatusInput, LineJob, LineJobStatusInput, Person, QuoteBundle, QuoteInput,
  QuoteStatusInput,
} from "../../types";

export interface ClientFinancePageModel {
  /** The client's jobs and quotes, its name, whether it is the agency's own, and today. */
  jobsQuery: UseQueryResult<ClientJobsResponse>;
  /** Who claims on each job, for the Who column. */
  claimsQuery: UseQueryResult<JobClaim[]>;
  invoicesQuery: UseQueryResult<ClientJobInvoicesResponse>;
  peopleQuery: UseQueryResult<Person[]>;
  updateQuote: UseMutationResult<QuoteBundle, Error, { quoteId: string; input: Partial<QuoteInput> }>;
  setStatus: UseMutationResult<LineJob, Error, { jobId: string; input: LineJobStatusInput }>;
  /** A quote row's status dropdown: any status but Active, which asks its day in the drawer. */
  setQuoteStatus: UseMutationResult<QuoteBundle, Error, { quoteId: string; input: QuoteStatusInput }>;
  /** An invoice row's status dropdown: Draft or Sent; Paid records a payment in the drawer. */
  setInvoiceStatus: UseMutationResult<JobInvoice, Error, { invoiceId: string; input: JobInvoiceStatusInput }>;
}

/** `reads.invoices`: the Invoices view or an invoice drawer (R6). */
export function useClientFinancePage(clientId: string | undefined, reads: { invoices: boolean }): ClientFinancePageModel {
  const jobsQuery = useClientJobs(clientId);
  const claimsQuery = useClientClaims(clientId);
  const invoicesQuery = useJobInvoices(reads.invoices ? clientId : undefined);
  const peopleQuery = usePeople();
  const updateQuote = useUpdateQuote();
  const setStatus = useSetJobStatus();
  const setQuoteStatus = useSetQuoteStatus();
  const setInvoiceStatus = useSetInvoiceStatus();
  return { jobsQuery, claimsQuery, invoicesQuery, peopleQuery, updateQuote, setStatus, setQuoteStatus, setInvoiceStatus };
}
